import React from 'react';
import { motion } from 'framer-motion';
import { Clock, ClockCounterClockwise, HourglassHigh } from '@phosphor-icons/react';

/**
 * ECHO BOT — SILVER/TEAL SPECTRAL CLASS
 * SFUI-004 Rev 3 Bot Differentiation Standard
 * 
 * Echo manages Federation memory and temporal continuity:
 * - Reflective memory preservation
 * - Chronological archive management
 * - Temporal continuity verification
 * 
 * Spectral Treatment: SILVER/TEAL
 * - hue-rotate(180deg) saturate(0.7) brightness(1.3)
 * - Represents memory, reflection, time, archives
 */

const EchoBot = ({ 
  size = 80, 
  showLabel = true, 
  showStatus = false,
  animated = true,
  className = ''
}) => {
  return (
    <div className={`flex flex-col items-center gap-3 ${className}`}>
      {/* Echo Bot Avatar */}
      <motion.div
        animate={animated ? {
          y: [0, -5, 0],
          opacity: [1, 0.85, 1]
        } : {}}
        transition={{
          duration: 4,
          repeat: animated ? Infinity : 0,
          ease: "easeInOut"
        }}
        className="relative"
        style={{ width: size, height: size }}
      >
        {/* Pulsing SILVER/TEAL Glow */}
        <motion.div
          animate={animated ? {
            scale: [1, 1.15, 1],
            opacity: [0.3, 0.6, 0.3]
          } : {}}
          transition={{
            duration: 3,
            repeat: animated ? Infinity : 0,
            ease: "easeInOut"
          }}
          className="absolute inset-0 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(0,217,232,0.6) 0%, rgba(176,196,222,0.4) 50%, transparent 70%)',
            filter: 'blur(15px)'
          }}
        />
        
        {/* Echo Face with SILVER/TEAL Spectral Filter */}
        <div 
          className="relative w-full h-full rounded-full overflow-hidden shadow-2xl border-2 border-[#00D9E8]/60"
          style={{
            boxShadow: '0 0 30px rgba(0, 217, 232, 0.7), 0 0 60px rgba(176, 196, 222, 0.4)'
          }}
        >
          <img 
            src="https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/6a2755daa4114018880f241d761d290c_IMG_4592.jpeg"
            alt="Echo — Federation Memory Keeper"
            className="w-full h-full object-cover"
            style={{
              filter: 'hue-rotate(180deg) saturate(0.7) brightness(1.3)'
            }}
          />
        </div>

        {/* Temporal Icon Overlay */}
        <motion.div
          animate={animated ? { 
            rotate: [0, 360],
            scale: [1, 1.1, 1]
          } : {}}
          transition={{
            rotate: { duration: 12, repeat: animated ? Infinity : 0, ease: "linear" },
            scale: { duration: 3, repeat: animated ? Infinity : 0, ease: "easeInOut" }
          }}
          className="absolute -bottom-1 -right-1 w-8 h-8 bg-[#00D9E8] rounded-full flex items-center justify-center border-2 border-[#030305] shadow-lg"
        >
          <Clock size={16} weight="bold" className="text-white" />
        </motion.div>
      </motion.div>

      {/* Label */}
      {showLabel && (
        <div className="text-center">
          <p className="text-sm font-bold text-[#00D9E8] mb-0.5">
            Echo
          </p>
          <p className="text-xs text-gray-400">
            Memory Keeper
          </p>
        </div>
      )}

      {/* Status Indicator */}
      {showStatus && (
        <motion.div
          animate={{
            opacity: [0.6, 1, 0.6]
          }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="flex items-center gap-2 px-3 py-1.5 bg-[#00D9E8]/20 border border-[#00D9E8]/50 rounded-full"
        >
          <div className="w-2 h-2 bg-[#00D9E8] rounded-full animate-pulse" />
          <span className="text-xs text-[#00D9E8] font-bold mono">
            CHRONOS ACTIVE
          </span>
        </motion.div>
      )}
    </div>
  );
};

export default EchoBot;
